import { format } from "date-fns";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { InvestObjectParametersContainerProps } from "./InvestObjectParameters.types";

import {
  CALCULATOR_STAGES,
  DEFAULT_LOAN_RATE,
  priceGrowthRelationOptions,
} from "constants/calculator";

import { getItemSelect } from "helpers/selectHelpers";
import { convertValueToLocaleForm } from "helpers/convertValueToLocale";

import InvestObjectParameters from "./InvestObjectParameters";

const InvestObjectParametersContainer = ({
  activeTab,
  isDesktop,
  defaultDowntimeDynamicNotEmpty,
  loans,
  isRootAdmin,
  calculatorType,
  loansOptions,
}: InvestObjectParametersContainerProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();

  const handleEnergyEfficiencyClassChange = (option: any) => {
    const nextSpecialLoan = loans.find(
      (loan: any) => loan.devName === option?.value
    );
    const period = nextSpecialLoan?.periods?.find(
      (period: any) =>
        period.from <= values.client.loanYears * 12 &&
        period.to >= values.client.loanYears * 12
    );

    const filteredLoans = values.loans.filter(
      (loan: any) => loan.name === "Classic"
    );

    const nextLoan = nextSpecialLoan && {
      name: option?.label,
      devName: nextSpecialLoan?.devName,
      startRate: convertValueToLocaleForm(period?.percent || DEFAULT_LOAN_RATE),
      rate: convertValueToLocaleForm(
        nextSpecialLoan?.rate || DEFAULT_LOAN_RATE
      ),
    };

    if (nextLoan) {
      setFieldValue("loans", [...filteredLoans, nextLoan]);
    } else {
      setFieldValue("loans", filteredLoans);
    }

    if (option?.value !== "QNG") {
      setFieldValue("isSonderAfaForced", {
        label: "Nein",
        value: false,
      });
    }
  };

  const handlePriceGrowthRelationTypeChange = (option: any) => {
    const priceGrowthDate =
      values.stages.find((stage) => stage.type === option?.value)?.date ||
      format(new Date(), "yyyy-MM-dd");

    setFieldValue("priceGrowth.date", priceGrowthDate);
  };

  const handleOrderDateChange = (purchaseDate: string) => {
    const purchaseDateValue = new Date(purchaseDate);
    const purchaseYearMonth =
      purchaseDateValue.getFullYear() * 12 + purchaseDateValue.getMonth();

    const finishDateValue = new Date(values.stages[2].date);
    const finishYearMonth =
      finishDateValue.getFullYear() * 12 + finishDateValue.getMonth();

    if (purchaseYearMonth > finishYearMonth) {
      setFieldValue("priceGrowth.date", purchaseDate);
      setFieldValue(
        "priceGrowth.relationType",
        getItemSelect(
          priceGrowthRelationOptions(t),
          CALCULATOR_STAGES.PURCHASE_STAGE
        )
      );

      return;
    }

    if (
      values.priceGrowth.relationType.value === CALCULATOR_STAGES.PURCHASE_STAGE
    ) {
      setFieldValue("priceGrowth.date", purchaseDate);
    }
  };

  const handleFinishDateChange = (finishDate: string) => {
    const purchaseDateValue = new Date(values.stages[1].date);
    const purchaseYearMonth =
      purchaseDateValue.getFullYear() * 12 + purchaseDateValue.getMonth();

    const finishDateValue = new Date(finishDate);
    const finishYearMonth =
      finishDateValue.getFullYear() * 12 + finishDateValue.getMonth();

    if (purchaseYearMonth > finishYearMonth) {
      setFieldValue("priceGrowth.date", purchaseDateValue);
      setFieldValue(
        "priceGrowth.relationType",
        getItemSelect(
          priceGrowthRelationOptions(t),
          CALCULATOR_STAGES.PURCHASE_STAGE
        )
      );

      return;
    }

    if (
      values.priceGrowth.relationType.value === CALCULATOR_STAGES.FINISH_STAGE
    ) {
      setFieldValue("priceGrowth.date", finishDate);
    }
  };

  return (
    <InvestObjectParameters
      activeTab={activeTab}
      isDesktop={isDesktop}
      isRootAdmin={isRootAdmin}
      defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
      calculatorType={calculatorType}
      loansOptions={loansOptions}
      onOrderDateChange={handleOrderDateChange}
      onFinishDateChange={handleFinishDateChange}
      onEnergyEfficiencyClassChange={handleEnergyEfficiencyClassChange}
      onPriceGrowthRelationTypeChange={handlePriceGrowthRelationTypeChange}
    />
  );
};

export default InvestObjectParametersContainer;
